// CRM layer — "فتح فرصة" flow.
//
// Phase 6-D: a client PROFILE WITH A NAME is now mandatory before an
// opportunity can exist, and the server enforces it (POST /opportunities
// returns 400 `missing_client_profile`). This modal therefore has two steps:
//
//   step "profile"  — only when the number has no named profile yet: collect
//                     the client's name and create it via PUT /clients/:phone
//   step "type"     — the normal flow: queue-derived suggestion, pick a type,
//                     add details, create
//
// A number that already has a named profile goes straight to "type".
// Creating an opportunity is always deliberate: nothing is written until the
// agent confirms, and there is no pending/suggested limbo state.

import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  crmSuggest, crmGetTypes, crmCreateOpportunity, crmGetClient, crmUserNames,
  STAGE_LABEL, stagePillClass, typeLabel
} from './crmApi';
import { hasPerm } from './perms';
import CrmClientNameForm from './CrmClientNameForm';

const CrmStartOpportunityModal = ({ phoneNumber, queue, sourceCallId, onClose, onCreated, onOpenOpportunity }) => {
  const { user } = useAuth();
  const canAssign = hasPerm(user, 'assign_opportunity');   // manager: create AND assign
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('type');          // 'profile' | 'type'
  const [normalizedPhone, setNormalizedPhone] = useState(phoneNumber);
  const [suggestion, setSuggestion] = useState(null);
  const [types, setTypes] = useState([]);
  const [type, setType] = useState('');
  const [details, setDetails] = useState('');
  const [clientName, setClientName] = useState('');
  const [existingClient, setExistingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Item 1: managers may assign the new opportunity to a chosen agent. Empty =
  // assign to themselves (the default the backend already applies).
  const [assignTo, setAssignTo] = useState('');
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    if (!canAssign) return;
    crmUserNames().then((r) => setAgents(r.data.users || [])).catch(() => setAgents([]));
  }, [canAssign]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      crmSuggest(phoneNumber, queue),
      crmGetTypes(),
      crmGetClient(phoneNumber).catch(() => ({ data: { client: null } }))
    ])
      .then(([s, t, c]) => {
        if (!alive) return;
        setSuggestion(s.data);
        setTypes(t.data.types);
        setNormalizedPhone(s.data.phone_number || phoneNumber);
        setType(s.data.suggestedType || (t.data.types[0] && t.data.types[0].type_name) || '');

        const client = c.data.client;
        setExistingClient(client);
        const named = client && String(client.full_name || '').trim();
        // Gate on a NAME, not merely on a row existing.
        setStep(named ? 'type' : 'profile');
        if (client && client.full_name) setClientName(client.full_name);
      })
      .catch((e) => alive && setError(e.response?.data?.error || 'تعذر جلب البيانات'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [phoneNumber, queue]);

  // Profile creation now lives entirely in CrmClientNameForm — see step 1.

  const submit = async () => {
    setError('');
    if (!type) { setError('اختر نوع الطلب'); return; }
    setSaving(true);
    try {
      const res = await crmCreateOpportunity({
        phone: normalizedPhone,
        opportunity_type: type,
        request_details: details,
        source_call_id: sourceCallId || undefined,
        // Honoured by the backend only for assign_opportunity holders; otherwise
        // it defaults to the creator.
        assigned_to_extension: (canAssign && assignTo) ? assignTo : undefined
      });
      if (onCreated) onCreated(res.data.opportunity);
      onClose();
    } catch (e) {
      // 400 missing_client_profile = the server's guard; send them back a step.
      if (e.response?.data?.reason === 'missing_client_profile') {
        setStep('profile');
        setError('يجب إدخال اسم العميل أولاً');
      } else {
        // 409 = an active request of this type already exists (uniqueness rule)
        setError(e.response?.data?.error || 'فشل إنشاء الطلب');
      }
      setSaving(false);
    }
  };

  const active = suggestion?.opportunities || [];

  return (
    <div className="modal-overlay crm-calm" onClick={onClose}>
      <div className="modal crm-scope" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{step === 'profile' ? 'ملف العميل مطلوب أولاً' : 'فتح فرصة جديدة'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="crm-empty">جاري التحميل...</div>
        ) : (
          <div>
            <div className="crm-modal-phone">
              <div className="num">
                {normalizedPhone}
              </div>
              {queue && <div className="crm-modal-note" style={{ fontSize: 12, marginTop: 3 }}>الطابور: {queue}</div>}
              {step === 'type' && suggestion?.suggestedType && (
                <div className="crm-modal-note" style={{ fontSize: 12, marginTop: 3 }}>
                  النوع المقترح من الطابور: <b>{suggestion.suggestedType}</b>
                  {suggestion.suggestedDepartment ? ` — قسم ${suggestion.suggestedDepartment}` : ''}
                </div>
              )}
            </div>

            {/* ---------- STEP 1: profile ----------
                Uses the SAME form component as the "حفظ العميل" button on a
                call row, so profile creation behaves identically wherever it
                is triggered. */}
            {step === 'profile' && (
              <>
                {error && <div className="crm-modal-err">{error}</div>}
                <CrmClientNameForm
                  phoneNumber={normalizedPhone}
                  initialName={clientName}
                  submitLabel="حفظ ومتابعة"
                  autoFocus
                  note={existingClient
                    ? 'يوجد ملف لهذا الرقم لكنه بلا اسم — أدخل الاسم لإكماله ثم تابع اختيار نوع الطلب.'
                    : 'لا يمكن فتح طلب بدون ملف عميل يحمل اسماً. أدخل اسم العميل الآن، ثم تابع اختيار نوع الطلب.'}
                  onSaved={() => { setError(''); setStep('type'); }}
                  onCancel={onClose}
                />
              </>
            )}

            {/* ---------- STEP 2: type + details ---------- */}
            {step === 'type' && (
              <>
                {active.length > 0 && (
                  <div className="crm-modal-active">
                    <div className="h">لهذا العميل طلبات نشطة بالفعل:</div>
                    {active.map((o) => (
                      <div key={o.id} className="row">
                        <span className={stagePillClass(o.crm_stage)}>{STAGE_LABEL[o.crm_stage]}</span>
                        <span style={{ fontSize: 13 }}>{typeLabel(o.opportunity_type)}</span>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={() => { onClose(); if (onOpenOpportunity) onOpenOpportunity(o.id); }}>
                          فتح
                        </button>
                      </div>
                    ))}
                    <div className="fine">
                      يمكنك فتح طلب من نوع مختلف، لكن لا يمكن فتح طلبين نشطين من نفس النوع.
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: 12 }}>
                  <label className="crm-modal-label">نوع الطلب</label>
                  <select className="filter-select" style={{ width: '100%' }} value={type} onChange={(e) => setType(e.target.value)}>
                    {types.map((t) => (
                      <option key={t.id} value={t.type_name}>
                        {t.type_name}{t.department ? ` — ${t.department}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label className="crm-modal-label">تفاصيل الطلب</label>
                  <textarea className="filter-input" style={{ width: '100%', minHeight: 80 }}
                    placeholder="ماذا يطلب العميل بالضبط؟" value={details} onChange={(e) => setDetails(e.target.value)} />
                </div>

                {/* Item 1: manager-only — assign to a chosen agent at creation. */}
                {canAssign && (
                  <div style={{ marginBottom: 12 }}>
                    <label className="crm-modal-label">إسناد إلى موظف (اختياري)</label>
                    <select className="filter-select" style={{ width: '100%' }} value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                      <option value="">إسناد لي (افتراضي)</option>
                      {agents.map((a) => <option key={a.extension} value={a.extension}>{a.fullName} ({a.extension})</option>)}
                    </select>
                  </div>
                )}

                {error && <div className="crm-modal-err">{error}</div>}

                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button className="btn btn-primary" disabled={saving} onClick={submit}>
                    {saving ? 'جارٍ الإنشاء...' : 'فتح الطلب'}
                  </button>
                  <button className="btn btn-outline" onClick={onClose}>إلغاء</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CrmStartOpportunityModal;
